// Customer admin contact tags: who to contact at a customer for what. Hints, not permissions.
// Mirrors AdminContactTags in CustomerAdminTagsData.cs (presets only, the API rejects anything else).
// See docs/reports/customer-admin-contact-tags.md

export type AdminContactTagName = "Atlas" | "CreatorVisits" | "Billing" | "Int" | "Agency";

export const adminContactTagNames: Array<AdminContactTagName> = ["Atlas", "CreatorVisits", "Billing", "Int", "Agency"];

// Mirrors AdminContactTagSource in AdminContactTagResolver.cs
export type AdminContactTagSource = "Activity" | "Stripe" | "Manual";

// Mirrors AdminTagOverride in CustomerAdminTagsData.cs
export type AdminContactTagOverride = "On" | "Off";

export const adminContactTagLabels: Record<AdminContactTagName, string> = {
  Atlas: "Atlas",
  CreatorVisits: "Creator Visits",
  Billing: "Billing",
  Int: "Integration",
  Agency: "Agency",
};

export const adminContactTagDescriptions: Record<AdminContactTagName, string> = {
  Atlas: "Took actions in Atlas (edited/approved/scheduled posts, connected social accounts, set weekly guidance)",
  CreatorVisits: "Created, sent to creators, chose a creator for, or marked complete a Creator Visit",
  Billing: "Their email is the customer's Stripe billing email",
  Int: "Website widget/integration contact (set by hand)",
  Agency: "Works at an agency the customer hired, not customer staff (set by hand)",
};

export const adminContactTagColors: Record<AdminContactTagName, string> = {
  Atlas: "#dbe9ff",
  CreatorVisits: "#dcf2dc",
  Billing: "#fff0cc",
  Int: "#eee0ff",
  Agency: "#ffe0e6",
};

// A shown tag on the /super Customer Contacts page (CustomerContactTag in CustomerContactsBuilder.cs)
export interface CustomerContactTag {
  tag: AdminContactTagName;
  source: AdminContactTagSource | null;
  lastSeenOn: string | null;
}

// One (admin, customer) row on the /super Customer Contacts page (CustomerContactRow in CustomerContactsBuilder.cs)
export interface CustomerContactRow {
  customerId: string;
  customerName: string | null;
  isTestCustomer: boolean;
  isDisabledCustomer: boolean;
  authId: string | null;
  sherpaId: string | null;
  name: string | null;
  email: string | null;
  lastLogin: string | null;
  createdOn: string | null;
  adminSince: string | null;
  isShrpaTeam: boolean;
  isBillingOnly: boolean;
  tags: Array<CustomerContactTag>;
}

// All presets (on or off) for one admin, for the User Admin debug UI (ResolvedAdminContactTag in AdminContactTagResolver.cs)
export interface ResolvedAdminContactTag {
  tag: AdminContactTagName;
  isOn: boolean;
  hasSignal: boolean;
  source: AdminContactTagSource | null;
  firstSeenOn: string | null;
  lastSeenOn: string | null;
  override: AdminContactTagOverride | null;
  overrideBy: string | null;
  overrideOn: string | null;
}

/**
 * Compact age for a chip, ex. "today", "3d", "4mo", "2y"
 */
export function formatTagAge(isoDate: string | null | undefined, now: Date = new Date()): string {
  if (!isoDate) return "";
  const days = Math.floor((now.getTime() - new Date(isoDate).getTime()) / 86_400_000);
  if (days < 1) return "today";
  if (days < 60) return `${days}d`;
  if (days < 730) return `${Math.round(days / 30)}mo`;
  return `${Math.round(days / 365)}y`;
}
